import type { CoilStackResponse } from "@/storageAreas/api/models/storageAreaResponse";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export interface EditCoilStack {
  storageAreaId: string;
  coilStack: CoilStackResponse
}

export interface CopyCoilStack {
  coilStack: CoilStackResponse
}

export const useSelectedCoilStackStore = defineStore('selectedCoilStackStore', () => {

  const operation = ref<'create'|'edit'|'none'>('none');

  const model = ref<CoilStackResponse>();

  const defaultSlots = 5;
  const slots = ref<number>(defaultSlots);
  const slotsInitial = computed(() => {
    if (!model.value) return;
    return model.value.slots;
  });
  const slotsChanged = computed(() =>
    operation.value === 'edit' && slots.value !== slotsInitial.value
  );

  const defaultSlotWidth = 1200;
  const slotWidth = ref<number>(defaultSlotWidth);
  const slotWidthInitial = computed(() => {
    if (!model.value) return;
    return model.value.slotWidth;
  });
  const slotWidthChanged = computed(() =>
    operation.value === 'edit' && slotWidth.value !== slotWidthInitial.value
  );

  const defaultMaxLevels = 3;
  const maxLevels = ref<number|null>(defaultMaxLevels);
  const maxLevelsInitial = computed(() => {
    if (!model.value) return;
    return model.value.maxLevels;
  });
  const maxLevelsChanged = computed(() =>
    operation.value === 'edit' && maxLevels.value !== maxLevelsInitial.value
  );

  const defaultMaxHeight = null;
  const maxHeight = ref<number|null>(defaultMaxHeight);
  const maxHeightInitial = computed(() => {
    if (!model.value) return;
    return model.value.maxHeight;
  });
  const maxHeightChanged = computed(() =>
    operation.value === 'edit' && maxHeight.value !== maxHeightInitial.value
  );

  const defaultMaxCoilDiameter = null;
  const maxCoilDiameter = ref<number|null>(defaultMaxCoilDiameter);
  const maxCoilDiameterInitial = computed(() => {
    if (!model.value) return;
    return model.value.maxCoilDiameter;
  })
  const maxCoilDiameterChanged = computed(() =>
    operation.value === 'edit' && maxCoilDiameter.value !== maxCoilDiameterInitial.value);

  const changed = computed(() =>
    slotsChanged.value || slotWidthChanged.value || maxHeightChanged.value || maxLevelsChanged.value || maxCoilDiameterChanged.value
  );

  function create() {
    reset();
    operation.value = 'create';
  }

  function edit(action: EditCoilStack) {
    reset();
    const coilStack = action.coilStack;
    fill(coilStack);
    model.value = coilStack;
    operation.value = 'edit';
  }

  function copy(action: CopyCoilStack) {
    reset();
    const coilStack = action.coilStack;
    fill(coilStack);
    operation.value = 'create';
  }

  function hide() {
    reset();
  }

  function reset() {
    operation.value = 'none';
    model.value = undefined;
    setDefaults();
  }

  function setDefaults() {
    slots.value = defaultSlots;
    slotWidth.value = defaultSlotWidth;
    maxLevels.value = defaultMaxLevels;
    maxHeight.value = defaultMaxHeight;
    maxCoilDiameter.value = defaultMaxCoilDiameter;
  }

  function fill(coilStack: CoilStackResponse) {
    slots.value = coilStack.slots;
    slotWidth.value = coilStack.slotWidth;
    maxLevels.value = coilStack.maxLevels;
    maxHeight.value = coilStack.maxHeight;
    maxCoilDiameter.value = coilStack.maxCoilDiameter;
  }

  return {
    create,
    edit,
    copy,
    hide,
    operation,
    slots,
    slotsChanged,
    slotWidth,
    slotWidthChanged,
    maxHeight,
    maxHeightChanged,
    maxLevels,
    maxLevelsChanged,
    maxCoilDiameter,
    maxCoilDiameterChanged,
    changed,
  };

});
